import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { portfolioData } from '../mock';
import { ArrowLeft } from 'lucide-react';
import { SketchStar } from '../components/SketchElements';
import { PhotoCard } from '../components/PhotoCard';
import { MaskingTape } from '../components/MaskingTape';

export const GalleryPage = () => {
  const { categoryId } = useParams();

  const categoryMap = {
    'gaming': 'gaming',
    'social-media': 'socialMedia',
    'illustration': 'illustration',
    'branding': 'branding',
    'video': 'video',
  };

  const projectsKey = categoryMap[categoryId];
  const projects = portfolioData.projects[projectsKey] || [];
  const category = portfolioData.categories.find((cat) => cat.id === categoryId);

  // Slight per-card rotation (1-3 degrees), alternating direction
  const cardRotations = [-3, 2, -2, 3, -1, 2, -2.5, 1.5];

  return (
    <div className="min-h-screen pt-24 pb-20 relative overflow-hidden">
      {/* Paper grain texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.12]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='280' height='280'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Scattered red notebook stars */}
      {[
        { top: '14%', left: '4%',  size: 18, rot: -10, delay: 0.2 },
        { top: '24%', right: '7%', size: 22, rot:  20, delay: 0.4 },
        { top: '54%', left: '3%',  size: 16, rot:  -5, delay: 0.6 },
        { top: '64%', right: '4%', size: 20, rot:  15, delay: 0.8 },
        { top: '84%', left: '8%',  size: 14, rot: -18, delay: 1.0 },
      ].map((s, i) => {
        const posStyle = {
          width: `${s.size}px`,
          height: `${s.size}px`,
          transform: `rotate(${s.rot}deg)`,
        };
        if (s.left) posStyle.left = s.left;
        if (s.right) posStyle.right = s.right;
        if (s.top) posStyle.top = s.top;
        return (
          <SketchStar
            key={i}
            className="absolute text-[color:var(--punk-red)]/80 hidden sm:block"
            style={posStyle}
            delay={s.delay}
          />
        );
      })}

      <div className="container mx-auto max-w-7xl px-6 relative z-10">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-10 group"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="font-marker text-base uppercase tracking-wide">back to home</span>
        </Link>

        {/* Title with red underline */}
        <div className="mb-12 animate-fade-in">
          <div className="relative inline-block">
            <h1 className="font-stencil text-5xl sm:text-6xl md:text-8xl text-white tracking-tight uppercase leading-[0.9]">
              {category?.title}
            </h1>
            {/* Red marker underline — extends past the text on both sides */}
            <svg
              viewBox="0 0 400 26"
              preserveAspectRatio="none"
              className="absolute -left-[6%] -bottom-3 w-[112%] h-5 md:h-7 pointer-events-none"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M2,14 C30,8 70,18 110,11 C150,5 195,18 240,10 C285,4 330,17 370,11 C385,9 395,12 398,11"
                stroke="var(--punk-red)"
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray="500"
                strokeDashoffset="500"
                style={{ animation: 'draw-path 1s cubic-bezier(0.65, 0, 0.35, 1) 0.4s forwards' }}
              />
              <path
                d="M5,17 C32,12 68,21 108,14 C148,9 192,21 238,13 C284,8 328,20 368,14 C384,12 394,15 396,14"
                stroke="var(--punk-red)"
                strokeWidth="5"
                strokeLinecap="round"
                opacity="0.65"
                strokeDasharray="500"
                strokeDashoffset="500"
                style={{ animation: 'draw-path 1s cubic-bezier(0.65, 0, 0.35, 1) 0.55s forwards' }}
              />
            </svg>
          </div>

          <p className="font-hand text-xl md:text-2xl text-white/75 mt-8 max-w-3xl">
            {category?.description}
          </p>
        </div>

        {/* Project Cards — masonry layout, each frame matches the image's natural aspect ratio */}
        <div
          className="columns-1 sm:columns-2 lg:columns-3 gap-8 md:gap-10 mt-16"
          data-testid="projects-grid"
        >
          {projects.map((project, index) => {
            const rot = cardRotations[index % cardRotations.length];
            const projectSlug = project.slug || project.id;
            return (
              <Link
                key={project.id}
                to={`/gallery/${categoryId}/${projectSlug}`}
                data-testid={`project-card-${project.id}`}
                className="group cursor-pointer animate-fade-in block mb-12 md:mb-14 break-inside-avoid"
                style={{
                  animationDelay: `${index * 0.08}s`,
                  opacity: 0,
                }}
              >
                {/* Card — natural aspect ratio, no crop */}
                <div
                  className="relative transition-all duration-500 ease-out group-hover:-translate-y-3 group-hover:scale-[1.03]"
                  style={{ transform: `rotate(${rot}deg)` }}
                >
                  <div
                    className="relative overflow-hidden"
                    style={{
                      boxShadow:
                        '0 20px 40px rgba(0,0,0,0.55), 0 6px 14px rgba(0,0,0,0.4)',
                    }}
                  >
                    <img
                      src={project.image}
                      alt={project.title}
                      loading="lazy"
                      className="w-full h-auto block"
                      style={{ filter: 'saturate(0.95) contrast(1.04)' }}
                    />
                    {/* Paper grain overlay */}
                    <div
                      className="absolute inset-0 pointer-events-none opacity-25"
                      style={{
                        backgroundImage:
                          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' seed='3'/><feColorMatrix values='0 0 0 0 0.1  0 0 0 0 0.08  0 0 0 0 0.06  0 0 0 0.32 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                        mixBlendMode: 'overlay',
                      }}
                    />
                  </div>
                </div>

                {/* Project meta below the card */}
                <div className="mt-5 px-1">
                  <h3 className="font-marker text-lg md:text-xl text-white uppercase tracking-wide group-hover:text-[color:var(--punk-red)] transition-colors">
                    {project.title}
                  </h3>
                  <p className="font-hand text-base md:text-lg text-white/65 mt-1">
                    {project.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {project.tools.map((tool, i) => (
                      <span
                        key={i}
                        className="font-typewriter text-[10px] uppercase tracking-widest text-white/70 px-2.5 py-1 border border-white/25"
                        style={{ transform: `rotate(${i % 2 === 0 ? -1 : 1}deg)` }}
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Empty state */}
        {projects.length === 0 && (
          <div className="text-center py-20">
            <p className="font-marker text-3xl text-white/50 uppercase">
              still sketching this chapter...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
